import { modalVariants } from '@shared/components/modal/Modals';
import { VariantProps } from 'class-variance-authority';
import { t } from 'i18next';
import { action, makeAutoObservable, observable } from 'mobx';
import React from 'react';
import { LoginPage } from '@user/pages';

export default class ModalStore {
    instances: ModalInstance[] = [];

    constructor() {
        makeAutoObservable(this, {
            instances: observable,
            showModal: action.bound,
            hideModal: action.bound,
            hideAllModals: action.bound,
            showAlertModal: action.bound,
            showLoginModal: action.bound
        });
    }

    showModal(options?: ModalInstance) {
        const instance = new ModalInstance(options);
        this.instances.push(instance);
    }

    hideModal(id?: string) {
        if (id) {
            this.instances = this.instances.filter(ins => ins.id !== id);
        } else {
            this.instances.splice(-1, 1);
        }
    }

    hideModals(ids: string[]) {
        this.instances = this.instances.filter(ins => !ins?.id || !ids.includes(ins.id));
    }

    hideAllModals() {
        this.instances = [];
    }

    showAlertModal({
        id,
        type,
        content,
        cancelButton,
        onCancel,
        saveButton,
        onSave,
        ...options
    }: ModalInstance = {}) {
        this.showModal({
            id,
            type,
            cancelButton: cancelButton ?? 'キャンセル',
            saveButton: saveButton ?? '確認',
            onCancel: () => {
                this.hideModal(id);
                onCancel?.();
            },
            onSave: () => {
                this.hideModal(id);
                onSave?.();
            },
            content: (
                <div className='text-center'>{content}</div>
            ),
            ...options
        });
    }

    showLoginModal() {
        this.showModal({
            id: 'form-login',
            size: '2xl',
            showHeader: false,
            footerClassName: 'hidden',
            contentClassName: '!p-0 overflow-hidden max-sm:w-full max-sm:h-full max-sm:max-w-none max-sm:m-0 max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0',
            content: <LoginPage />
        });
    }
}

class ModalInstance {
    id?: string;
    type?: VariantProps<typeof modalVariants>['type'];
    size?: VariantProps<typeof modalVariants>['size'];
    // header
    title?: string;
    titleClassName?: string;
    description?: string;
    headerRender?: React.ReactNode;
    showHeader?: boolean;
    // close button
    showCloseIcon?: boolean;
    // content
    content?: React.ReactNode;
    contentClassName?: string;
    closeWhenOutside?: boolean;
    // footer
    footer?: React.ReactNode;
    footerClassName?: string;
    cancelButton?: React.ReactNode;
    cancelButtonClassName?: string;
    onCancel?: Function;
    saveButton?: React.ReactNode;
    saveButtonClassName?: string;
    onSave?: Function;

    constructor({
        showCloseIcon = true,
        closeWhenOutside = false,
        showHeader = true,
        type = 'default',
        size = 'default',
        ...options
    }: ModalInstance = {}) {
        this.id = options.id;
        this.type = type;
        this.size = size;
        this.title = options.title;
        this.titleClassName = options.titleClassName;
        this.description = options.description;
        this.headerRender = options.headerRender;
        this.showHeader = showHeader;
        this.showCloseIcon = showCloseIcon;
        this.content = options.content;
        this.contentClassName = options.contentClassName;
        this.closeWhenOutside = closeWhenOutside;
        this.footer = options.footer;
        this.footerClassName = options.footerClassName;
        this.cancelButton = options.cancelButton;
        this.cancelButtonClassName = options.cancelButtonClassName;
        this.onCancel = options.onCancel;
        this.saveButton = options.saveButton;
        this.saveButtonClassName = options.saveButtonClassName;
        this.onSave = options.onSave;
    }
}
